import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { loginOutApi, getUserRole } from '@/api/login'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { MerchantDropdownType } from '@/components/MerchantDropdown'
// import { useStorage } from '@/hooks/web/useStorage'

// const { getStorage, setStorage } = useStorage('localStorage')

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  owUserInfo?: any
  roles: any
  merchants: MerchantDropdownType[] | undefined
  curMerchant: MerchantDropdownType | undefined
  resetUserPhone: string | undefined
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      owUserInfo: undefined,
      roles: undefined,
      merchants: undefined,
      curMerchant: undefined,
      resetUserPhone: undefined,
      loginInfo: undefined
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getOwUserInfo(): any | undefined {
      return this.owUserInfo
    },
    isSuperAdmin(): boolean {
      return this.owUserInfo?.memberType == '1'
    },
    getMerchants(): MerchantDropdownType[] | undefined {
      return this.merchants
    },
    getCurMerchant(): MerchantDropdownType | undefined {
      return this.curMerchant
    },
    getResetUserPhone(): string | undefined {
      return this.resetUserPhone
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
      // setStorage('token', token)
    },
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },
    setOwUserInfo(userInfo?: any) {
      this.owUserInfo = userInfo
    },
    setMerchants(merchants: MerchantDropdownType[]) {
      this.merchants = merchants
    },
    async setCurMerchant(merchant: MerchantDropdownType) {
      this.curMerchant = merchant
      await this.switchMerchant(this.curMerchant.id)
    },
    setResetUserPhone(phone: string) {
      this.resetUserPhone = phone
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          const res = await loginOutApi().catch(() => {})
          if (res) {
            this.reset()
          }
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setRoleRouters([])
      this.setOwUserInfo(undefined)
      this.roles = undefined
      this.merchants = undefined
      this.curMerchant = undefined
      this.resetUserPhone = undefined
      router.replace('/login')
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    async initRoles() {
      const storedMerchant_id = this.curMerchant?.id
      let merchant_id = this.owUserInfo?.staffMerchantIds[0].id
      // check user is the staff of stored merchant
      if (storedMerchant_id) {
        this.owUserInfo?.staffMerchantIds.forEach((merchant) => {
          if (merchant.id == storedMerchant_id) {
            merchant_id = storedMerchant_id
          }
        })
      }
      await this.switchMerchant(merchant_id)
      this.curMerchant = this.owUserInfo?.staffMerchantIds[0] as MerchantDropdownType
    },
    async switchMerchant(merchant_id: string) {
      this.roles = undefined
      const res = await getUserRole({
        member_id: this.owUserInfo?.id,
        merchant_id: merchant_id
      })
      this.roles = res.data.roles
      this.owUserInfo.roleName = res.data.name
    },
    hasPermission(point: string): boolean {
      if (this.isSuperAdmin) {
        // currently super admin is only allowed to visit super route.
        return point == 'super'
      }
      if (point == 'super') {
        // super is a special permission, only for super admin.
        return false
      }
      if (!this.roles) {
        return false
      }
      // or pointA pointB pointC, any of the three points makes the point accessible.
      if (point.startsWith('or ')) {
        const points = point.split(' ')
        return points.some((p) => {
          if (p && p != 'or') {
            if (this.roles[p] == undefined) {
              // Not premission control on this point
              return true
            }
            // 0: no access, 1: with access
            if (this.roles[p] > 0) {
              return true
            }
          }
        })
      }
      if (this.roles[point] == undefined) {
        // Not premission control on this point
        return true
      }
      // 0: no access, 1: with access
      if (this.roles[point] > 0) {
        return true
      }

      return false
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
